import axios from 'axios'
import { summarizeCalendarAlerts } from '../utils/calendarLogic'
import { getBulkPermanentDeleteResult, getBulkActionResult } from '../utils/mailBulkDeleteResult'

// Mail routes do real IMAP/DB work and can exceed the global 10s axios ceiling on
// large mailboxes — give reads 30s and bulk mutations 60s so a slow-but-working
// request isn't aborted and reported to the user as a failure.
const MAIL_READ_TIMEOUT = 30000
const MAIL_BULK_TIMEOUT = 60000

// ==================== API BASE URL HELPER ====================
// Stable helper that always returns correct API base URL
// Prevents hot reload issues and ensures consistent value
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:3001/api";

// Export API base URL for components that need it
export { API_BASE_URL }

// ==================== RETRY CONFIGURATION ====================
const MAX_RETRIES = 3
const RETRY_DELAY = 1000 // 1 second

// Track retry attempts per request
const retryCounts = new Map()

// ==================== AXIOS INSTANCE ====================
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
})

// ==================== REQUEST INTERCEPTOR ====================
api.interceptors.request.use(
  (config) => {
    // Attach token to every request - check both 'token' and 'access_token'
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Fallback: try to get token from user object
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user && user.token) {
            config.headers.Authorization = `Bearer ${user.token}`;
          } else {
            console.warn(`⚠️ API CALL: ${config.method?.toUpperCase()} ${config.url} | No token found in localStorage`);
          }
        } catch (e) {
          console.warn(`⚠️ API CALL: ${config.method?.toUpperCase()} ${config.url} | Error parsing user:`, e);
        }
      } else {
        console.warn(`⚠️ API CALL: ${config.method?.toUpperCase()} ${config.url} | No token and no user found`);
      }
    }
    
    // Reset retry count for new requests
    const requestKey = `${config.method}-${config.url}`
    retryCounts.set(requestKey, 0)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ==================== RESPONSE INTERCEPTOR ====================
api.interceptors.response.use(
  (response) => {
    // Clear retry count on success
    const requestKey = `${response.config.method}-${response.config.url}`
    retryCounts.delete(requestKey)
    
    // Detect HTML responses
    const contentType = response.headers['content-type'] || ''
    if (response.config?.responseType !== 'blob' && contentType.includes('text/html')) {
      throw new Error('Invalid API response — received HTML instead of JSON. Check that backend is running on port 3001.')
    }
    return response
  },
  async (error) => {
    const config = error.config
    const isAuthenticatedBlobDownload =
      config?.responseType === 'blob' &&
      (
        /\/guest-preferences\/[^/]+\/download$/.test(String(config?.url || '')) ||
        /\/lead-documents\/[^/]+\/download$/.test(String(config?.url || ''))
      )

    // H-12: 401 → token expired/invalid. Clear all stored credentials
    // and bounce the operator to /login. Operator console polling used
    // to silently swallow this, leaving the UI frozen on stale data.
    // Skipped if we're already on /login so we don't trap the user in
    // a redirect loop while they're trying to authenticate.
    if (error.response?.status === 401 && typeof window !== 'undefined' && !isAuthenticatedBlobDownload) {
      try {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      } catch (_) { /* storage may be unavailable */ }
      if (window.location && window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
      return Promise.reject(error)
    }

    // Don't retry if no config (request was never sent)
    if (!config) {
      return Promise.reject(error)
    }

    const requestKey = `${config.method}-${config.url}`
    const retryCount = retryCounts.get(requestKey) || 0
    
    // Check if error is connection-related and retries available
    const isConnectionError = 
      error.code === 'ECONNREFUSED' ||
      error.code === 'ERR_NETWORK' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('ERR_CONNECTION_REFUSED')
    
    if (isConnectionError && retryCount < MAX_RETRIES) {
      retryCounts.set(requestKey, retryCount + 1)
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
      
      // Retry the request
      return api(config)
    }
    
    // Clear retry count on final failure
    retryCounts.delete(requestKey)
    
    return Promise.reject(error)
  }
)

// Users (system users with login access)
export const getUsers = () => api.get('/users')
export const getUser = (id) => api.get(`/users/${id}`)
export const getCurrentUser = () => api.get('/users/me')

// Money-in/money-out Dashboard strip (Stage 1) — canonical derived figures, role-scoped server-side.
export const getMoneySummary = () => api.get('/dashboard/money-summary')
// Revenue graph (admin + managing_director only): per-month gross sales + VE commission,
// bucketable by check-in or booking-made date, for the given calendar year + prior year.
export const getRevenueGraph = (bucket = 'check_in', year) =>
  api.get('/dashboard/revenue-graph', { params: { bucket, ...(year ? { year } : {}) } })
export const createUser = (data) => api.post('/users', data)
export const updateUser = (id, data) => api.put(`/users/${id}`, data)
export const deleteUser = (id) => api.delete(`/users/${id}`)
export const getPermissionsList = () => api.get('/users/permissions/list')

// Repeat-guest: combined exclusive + non-exclusive booking history for a client.
export const getClientBookingHistory = (clientId) => api.get(`/clients/${clientId}/booking-history`)

// Audit log (admin-only). Returns { data, total, limit, offset }. Pass only set filters.
export const getAuditLogs = (params = {}) => api.get('/audit-logs', { params })

// WordPress → App sync (admin + managing_director). WordPress is the source of truth.
export const getWpSyncLast = () => api.get('/wp-sync/last')
export const startWpSyncPreview = () => api.post('/wp-sync/preview')
export const startWpSyncApply = () => api.post('/wp-sync/apply', { confirm: true }) // server also sets WP_SYNC_CONFIRM_ALL
export const getWpSyncStatus = (runId) => api.get(`/wp-sync/status/${runId}`)
export const getWpSyncLatest = () => api.get('/wp-sync/latest') // newest on-disk run — survives API restart / page reload
export const uploadUserAvatar = (file) => {
  const formData = new FormData()
  formData.append('avatar', file)
  // Must explicitly set Content-Type to undefined to let browser set multipart boundary
  return api.post('/users/avatar', formData, {
    headers: {
      'Content-Type': undefined
    },
    transformRequest: [(data) => data] // Prevent axios from transforming FormData
  })
}

// Villas
export const getVillas = (params = { limit: 2000 }) => api.get('/villas', { params })
// Lightweight villa list for dropdowns/pickers/filters — same /villas contract
// ({data,total,…}) but only identity/label/rate/commission fields + light
// owner/agent/agency joins (?view=picker). The full '*' list is ~9MB/16s for 469
// villas and trips the global 10s axios timeout; this is the one to use anywhere
// the villa record only feeds a <select>, filter, or label.
export const getVillaPickerOptions = (params = {}) => api.get('/villas', { params: { view: 'picker', limit: 500, ...params } })
export const getVilla = (id) => api.get(`/villas/${id}`)
export const createVilla = (data) => api.post('/villas', data)
export const updateVilla = (id, data) => api.put(`/villas/${id}`, data)
export const deleteVilla = (id) => api.delete(`/villas/${id}`)
// iCal "sync all" is a long sequential job (369 feeds, runs 15+ min) that always
// exceeds the global 10s ceiling. Give it a 60s per-call timeout so the abort is
// less premature — the backend keeps running and saves after the client
// disconnects, so the caller treats a timeout as "still running", not a failure.
// Body is {} (not null): the instance forces Content-Type: application/json, and
// express.json() strict mode rejects a "null" body with 400 before the route runs.
export const syncAllIcal = () => api.post('/villas/sync-ical/all', {}, { timeout: 60000 })

// Agents
export const getAgents = (params = { limit: 2000 }) => api.get('/agents', { params })
export const getAgent = (id) => api.get(`/agents/${id}`)
export const createAgent = (data) => api.post('/agents', data)
export const updateAgent = (id, data) => api.put(`/agents/${id}`, data)
export const deleteAgent = (id) => api.delete(`/agents/${id}`)

// Employees (commission tracking)
export const getEmployees = (includeInactive = false) => api.get(`/employees?includeInactive=${includeInactive}`)
export const getEmployee = (id) => api.get(`/employees/${id}`)
export const getEmployeeByUserId = (userId) => api.get(`/employees/by-user/${userId}`)
// Personal earnings view — backend derives the employee from the session (no id arg).
export const getMyCommission = () => api.get('/my-commission')
export const createEmployee = (data) => api.post('/employees', data)
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data)
export const deleteEmployee = (id) => api.delete(`/employees/${id}`)
export const getEmployeePayments = (id) => api.get(`/employees/${id}/payments`)
export const createEmployeePayment = (id, data) => api.post(`/employees/${id}/payments`, data)
export const getEmployeeCommissionSummary = (id) => api.get(`/employees/${id}/commission-summary`)

// Referral Agents (commission tracking)
export const getReferrals = (includeInactive = false) => api.get(`/referrals?includeInactive=${includeInactive}`)
export const getReferral = (id) => api.get(`/referrals/${id}`)
export const createReferral = (data) => api.post('/referrals', data)
export const updateReferral = (id, data) => api.put(`/referrals/${id}`, data)
export const deleteReferral = (id) => api.delete(`/referrals/${id}`)
export const getReferralPayments = (id) => api.get(`/referrals/${id}/payments`)
export const createReferralPayment = (id, data) => api.post(`/referrals/${id}/payments`, data)
export const getReferralCommissionSummary = (id) => api.get(`/referrals/${id}/commission-summary`)

// Owners
export const getOwners = (params = { limit: 2000 }) => api.get('/owners', { params })
export const getOwner = (id) => api.get(`/owners/${id}`)
export const createOwner = (data) => api.post('/owners', data)
export const updateOwner = (id, data) => api.put(`/owners/${id}`, data)
export const deleteOwner = (id) => api.delete(`/owners/${id}`)
export const convertOwnerToAgent = (id) => api.post(`/owners/${id}/convert-to-agent`)

// Agencies
export const getAgencies = (params = {}) => api.get('/agencies', { params })
export const getAgency = (id) => api.get(`/agencies/${id}`)
export const createAgency = (data) => api.post('/agencies', data)
export const updateAgency = (id, data) => api.put(`/agencies/${id}`, data)
export const deleteAgency = (id) => api.delete(`/agencies/${id}`)
export const setAgentAgency = (agentId, agency_id, role) =>
  api.put(`/agents/${agentId}/agency`, { agency_id, role })

// Vendors
export const getVendors = (params = {}) => api.get('/vendors', { params })
export const getVendor = (id) => api.get(`/vendors/${id}`)
export const createVendor = (data) => api.post('/vendors', data)
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data)
export const deleteVendor = (id) => api.delete(`/vendors/${id}`)

// ---- Change-request proposals (reservation_manager "Propose change" path) ----
// diffChangedFields: returns ONLY the keys whose value changed vs the loaded record, so a
// proposal carries exactly the edited fields (untouched excluded fields like commission_pct
// are naturally omitted). Array-aware; treats null/undefined/'' as equivalent (forms blank-fill).
export const diffChangedFields = (next = {}, prev = {}, keys = null) => {
  const out = {}
  const fields = keys || Object.keys(next)
  const norm = (v) => (v === undefined || v === null ? '' : v)
  for (const k of fields) {
    const a = next[k]
    const b = prev[k]
    const changed = (Array.isArray(a) || Array.isArray(b))
      ? JSON.stringify(a || []) !== JSON.stringify(b || [])
      : String(norm(a)) !== String(norm(b))
    if (changed) out[k] = a
  }
  return out
}

// submitChangeRequest: POST an edit proposal. Rejects (axios error) on 4xx — callers read
// err.response.data.error for the allowlist/409 message.
export const submitChangeRequest = ({ entity_type, op = 'edit', entity_id, proposed_fields }) =>
  api.post('/change-requests', { entity_type, op, entity_id, proposed_fields })

// Admin review of contact change-requests (the /approvals Contacts tab).
export const listChangeRequests = ({ status = 'submitted', entity_type } = {}) => {
  const q = new URLSearchParams()
  if (status) q.append('status', status)
  if (entity_type) q.append('entity_type', entity_type)
  const s = q.toString()
  return api.get(`/change-requests${s ? `?${s}` : ''}`)
}
export const getChangeRequest = (id) => api.get(`/change-requests/${id}`)
export const approveChangeRequest = (id) => api.post(`/change-requests/${id}/approve`, {})
export const rejectChangeRequest = (id, review_notes) =>
  api.post(`/change-requests/${id}/reject`, { review_notes: review_notes || null })

// Contact type conversions (owner ↔ agent ↔ vendor)
export const moveContact = (sourceTable, destTable, sourceId) =>
  api.post('/contacts/move', { sourceTable, destTable, sourceId })

// Merge two contacts of the same table (primary survives, duplicate is deleted)
export const mergeContacts = (table, primaryId, duplicateId) =>
  api.post('/contacts/merge', { table, primaryId, duplicateId })

// Find probable duplicate contacts across all tables
export const findDuplicates = () => api.get('/contacts/duplicates')

// CRM data quality audit
export const getDataQuality = () => api.get('/data-quality')

// Villa owner research (manual, human-approved)
export const getResearchQueue = () => api.get('/villa-research/queue')
export const fetchResearchCandidates = (villaId, url) => api.get(`/villa-research/${villaId}/fetch`, { params: { url } })
export const fetchPlacesCandidates = (villaId) => api.get(`/villa-research/${villaId}/places`)
export const fetchWebSearch = (villaId) => api.get(`/villa-research/${villaId}/search`)
export const saveResearchOwner = (villaId, payload) => api.post(`/villa-research/${villaId}/save-owner`, payload)

// Batch-research review queue (pre-fetched drafts awaiting approve/reject)
export const enrichVillaLive      = (villaId) => api.get(`/villa-research/${villaId}/enrich`)
export const browseResearch       = () => api.get('/villa-research/browse')
export const getReviewQueue       = () => api.get('/villa-research/review-queue')
export const approveResearchDraft = (villaId, payload) => api.post(`/villa-research/${villaId}/approve`, payload)
export const rejectResearchDraft  = (villaId, reason) => api.post(`/villa-research/${villaId}/reject`, { reason })

// Global search across villas + all contact tables
export const globalSearch = (q) => api.get('/search/global', { params: { q } })

// Destinations
export const getDestinations = () => api.get('/destinations')
export const importOwners = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  // Do NOT set Content-Type header — the browser must set the multipart boundary
  return api.post('/owners/import', formData)
}

// Categories
export const getCategories = () => api.get('/categories')
export const getCategory = (id) => api.get(`/categories/${id}`)
export const createCategory = (data) => api.post('/categories', data)
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data)
export const deleteCategory = (id) => api.delete(`/categories/${id}`)

// Amenities
export const getAmenities = () => api.get('/amenities')
export const getAmenity = (id) => api.get(`/amenities/${id}`)
export const createAmenity = (data) => api.post('/amenities', data)
export const updateAmenity = (id, data) => api.put(`/amenities/${id}`, data)
export const deleteAmenity = (id) => api.delete(`/amenities/${id}`)

// Seasonal Prices (deprecated - use SeasonalRates with BedroomTiers instead)
export const getVillaSeasons = (villaId) => api.get(`/villas/${villaId}/seasons`)
export const createVillaSeason = (villaId, data) => api.post(`/villas/${villaId}/seasons`, data)
export const updateSeasonalPrice = (id, data) => api.put(`/seasons/${id}`, data)
export const deleteSeasonalPrice = (id) => api.delete(`/seasons/${id}`)
export const updateSeason = (id, data) => api.put(`/seasons/${id}`, data)
export const deleteSeason = (id) => api.delete(`/seasons/${id}`)

// Bedroom Tiers
export const getVillaTiers = (villaId) => api.get(`/villas/${villaId}/tiers`)
export const createVillaTier = (villaId, data) => api.post(`/villas/${villaId}/tiers`, data)
export const updateTier = (id, data) => api.put(`/tiers/${id}`, data)
export const deleteTier = (id) => api.delete(`/tiers/${id}`)

// Seasonal Rates
export const getTierRates = (tierId) => api.get(`/tiers/${tierId}/rates`)
export const createTierSeason = (tierId, data) => api.post(`/tiers/${tierId}/seasons`, data)
export const updateSeasonRate = (id, data) => api.put(`/seasons/${id}`, data)
export const deleteSeasonRate = (id) => api.delete(`/seasons/${id}`)

// Image Management
export const deleteImage = (filename) => api.delete(`/upload/${filename}`)

// Exclusive Bookings
export const createExclusiveBooking = (villaId, data) => 
  api.post(`/exclusive-bookings`, { ...data, villaId })

export const getExclusiveBookings = (villaId, params = {}) => {
  const queryParams = new URLSearchParams()
  if (params.start) queryParams.append('start', params.start)
  if (params.end) queryParams.append('end', params.end)
  const queryString = queryParams.toString()
  return api.get(`/villas/${villaId}/exclusive-bookings${queryString ? `?${queryString}` : ''}`)
}

export const getExclusiveBookingsByEmployee = (employeeId, params = {}) => {
  const queryParams = new URLSearchParams()
  if (params.start) queryParams.append('start', params.start)
  if (params.end) queryParams.append('end', params.end)
  const queryString = queryParams.toString()
  return api.get(`/exclusive-bookings/by-employee/${employeeId}${queryString ? `?${queryString}` : ''}`)
}

export const getExclusiveBooking = (bookingId) =>
  api.get(`/exclusive-bookings/${bookingId}`)

// Read-only booking-first workflow context (PR A1 backend). Returns
// { data: { bookingType, booking, client, villa, lead, contracts, invoices,
// documentsCount, preferenceStatus } }. reservation_manager receives an
// operational allowlist only (no money / Stripe / DocuSign internals).
export const getBookingWorkflow = (bookingType, bookingId) => {
  const normalized = String(bookingType || '').trim().toLowerCase().replace('-', '_')
  return api.get(`/bookings/${normalized}/${bookingId}/workflow`)
}

export const updateExclusiveBooking = (bookingId, data) => 
  api.put(`/exclusive-bookings/${bookingId}`, data)

export const updateExclusiveBookingNotes = (bookingId, notes) =>
  api.put(`/exclusive-bookings/${bookingId}/notes`, { notes })

// Advance a booking's handoff pipeline stage. Role rules enforced server-side
// (reservation_manager may set back/operational stages only; 403 otherwise).
export const updateExclusiveBookingWorkflowStage = (bookingId, workflow_stage) =>
  api.put(`/exclusive-bookings/${bookingId}/workflow-stage`, { workflow_stage })

export const updateNonExclusiveBookingWorkflowStage = (bookingId, workflow_stage) =>
  api.put(`/non-exclusive-bookings/${bookingId}/workflow-stage`, { workflow_stage })

export const deleteExclusiveBooking = (bookingId) => 
  api.delete(`/exclusive-bookings/${bookingId}`)

// Payments
export const createPayment = (bookingId, data) => 
  api.post(`/exclusive-bookings/${bookingId}/payments`, data)

export const getPayments = (bookingId) => 
  api.get(`/exclusive-bookings/${bookingId}/payments`)

export const getPayment = (paymentId) => 
  api.get(`/payments/${paymentId}`)

export const updatePayment = (paymentId, data) => 
  api.put(`/payments/${paymentId}`, data)

export const deletePayment = (paymentId) => 
  api.delete(`/payments/${paymentId}`)

// Financial Summary
export const getFinancialSummary = (bookingId) => 
  api.get(`/exclusive-bookings/${bookingId}/financial-summary`)

export const getBookingFinancials = (bookingId) => 
  api.get(`/exclusive-bookings/${bookingId}/financials`)

export const recalculateBalance = (bookingId) => 
  api.post(`/exclusive-bookings/${bookingId}/recalculate-balance`)

// iCal Export
export const exportICalFeed = (villaId) =>
  api.get(`/villas/${villaId}/exclusive-ical-feed`, {
    responseType: 'blob' // Important for downloading files
  })

// Owner Payouts
export const addOwnerPayout = (bookingId, data) =>
  api.post(`/exclusive-bookings/${bookingId}/owner-payouts`, data)

export const getOwnerPayoutsForBooking = (bookingId) =>
  api.get(`/exclusive-bookings/${bookingId}/owner-payouts`)

// Exclusive Ribbon Calendar — timeline read + villa_blocks CRUD.
// includeExternal: opt-in Tokeet/External-iCal reference layer (default OFF).
export const getExclusiveCalendar = (start, end, includeExternal = false) =>
  api.get(`/exclusive-calendar?start=${start}&end=${end}${includeExternal ? '&includeExternal=true' : ''}`)

export const getExclusiveCalendarList = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString()
  return api.get(`/exclusive-calendar/list${qs ? `?${qs}` : ''}`)
}

export const getExclusiveShareLinks = () => api.get('/exclusive-calendar/share-links')

export const getExclusiveDashboardSummary = (window = 'this_month') =>
  api.get(`/exclusive-dashboard/summary?window=${encodeURIComponent(window)}`)

// Guest Preference Sheets
export const getGuestPreferenceInvitationStatuses = (bookingIds = []) => {
  const seen = new Set()
  const ids = []

  for (const bookingId of Array.isArray(bookingIds) ? bookingIds : []) {
    const id = Number(bookingId)
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
    if (ids.length >= 200) break
  }

  if (ids.length === 0) {
    return Promise.resolve({ data: { data: {} } })
  }

  return api.get(`/guest-preferences/invitations?bookingIds=${ids.join(',')}`)
}

export const getGuestPreferences = (params = {}) => {
  const queryParams = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value != null && value !== '')
  )
  const queryString = queryParams.toString()
  return api.get(`/guest-preferences${queryString ? `?${queryString}` : ''}`)
}

export const getGuestPreference = (id) =>
  api.get(`/guest-preferences/${encodeURIComponent(id)}`)

export const downloadGuestPreference = (id) =>
  api.get(`/guest-preferences/${encodeURIComponent(id)}/download`, { responseType: 'blob' })

export const deleteGuestPreference = (id) =>
  api.delete(`/guest-preferences/${encodeURIComponent(id)}`)

export const updateGuestPreferenceStatus = (id, status) =>
  api.patch(`/guest-preferences/${encodeURIComponent(id)}/status`, { status })

export const sendGuestPreferenceInvite = ({
  bookingId,
  recipientEmail,
  recipientName,
  message,
  attachments = []
}) => {
  const formData = new FormData()

  if (bookingId != null && bookingId !== '') formData.append('bookingId', bookingId)
  if (recipientEmail) formData.append('recipientEmail', recipientEmail)
  if (recipientName) formData.append('recipientName', recipientName)
  if (message) formData.append('message', message)
  Array.from(attachments || []).forEach((file) => {
    if (file && (typeof File === 'undefined' || file instanceof File)) {
      formData.append('attachments', file)
    }
  })

  return api.post('/guest-preferences/send-invite', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const getGuestPreferenceDownloadUrl = (id) => {
  const baseUrl = String(api.defaults.baseURL || API_BASE_URL).replace(/\/$/, '')
  return `${baseUrl}/guest-preferences/${encodeURIComponent(id)}/download`
}

export const createVillaBlock = (body) => api.post('/villa-blocks', body)
export const updateVillaBlock = (id, body) => api.patch(`/villa-blocks/${id}`, body)
export const deleteVillaBlock = (id) => api.delete(`/villa-blocks/${id}`)

export const getOwnerPayouts = () =>
  api.get('/owner-payouts')

export const getOwnerPayoutsByVilla = (villaId) =>
  api.get(`/owner-payouts?villaId=${villaId}`)

export const getAllOwnerPayouts = () =>
  api.get('/owner-payouts') // Alias for backward compatibility

export const updateOwnerPayout = (payoutId, data) =>
  api.put(`/owner-payouts/${payoutId}`, data)

export const deleteOwnerPayout = (payoutId) =>
  api.delete(`/owner-payouts/${payoutId}`)

// Notifications
export const getNotifications = (filters = {}) => {
  const params = new URLSearchParams()
  if (filters.is_read !== undefined) params.append('is_read', filters.is_read)
  if (filters.type) params.append('type', filters.type)
  return api.get(`/notifications?${params.toString()}`)
}

export const getUnreadNotificationCount = () =>
  api.get('/notifications/unread-count')

export const markNotificationAsRead = (notificationId) =>
  api.put(`/notifications/${notificationId}/read`)

export const markAllNotificationsAsRead = () =>
  api.put('/notifications/read-all')

export const deleteNotification = (notificationId) =>
  api.delete(`/notifications/${notificationId}`)

export const deleteAllNotifications = () =>
  api.delete('/notifications')

// Push Notifications
export const getPushStatus = () =>
  api.get('/push/status')

export const getMyPushSubscriptions = () =>
  api.get('/push/subscriptions/me')

export const getMyPushDeliveries = (limit = 20) =>
  api.get('/push/deliveries/me', { params: { limit } })

export const deletePushSubscription = (subscriptionId) =>
  api.delete(`/push/subscriptions/${encodeURIComponent(subscriptionId)}`)

export const sendPushTest = () =>
  api.post('/push/test')

export const subscribePush = (subscription) =>
  api.post('/push/subscribe', subscription)

export const unsubscribePush = (endpoint) =>
  api.post('/push/unsubscribe', { endpoint })

export const sendPushNotify = (payload) =>
  api.post('/push/notify', payload)

// Owner Monthly Reports
export const generateOwnerReport = (data) =>
  api.post('/owner-reports/generate', data)

export const deleteOwnerReport = (reportId) =>
  api.delete(`/owner-reports/${reportId}`)

export const getOwnerReports = (filters = {}) => {
  const params = new URLSearchParams()
  if (filters.owner_id) params.append('owner_id', filters.owner_id)
  if (filters.month) params.append('month', filters.month)
  if (filters.report_type) params.append('report_type', filters.report_type)
  return api.get(`/owner-reports?${params.toString()}`)
}

export const emailOwnerReport = (reportId, payload = {}) =>
  api.post(`/owner-reports/${reportId}/email`, payload)

export const getOwnerReportData = (reportId) =>
  api.get(`/owner-reports/${reportId}/data`)

export const getBrandingSettings = () =>
  api.get('/settings/branding')

export const uploadBrandingLogo = (formData) =>
  // Do NOT set Content-Type header — the browser must set the multipart boundary
  api.post('/settings/branding/logo', formData)

// Reminders
export const seedInsuranceReminders = () =>
  api.post('/reminders/seed-insurance')

export const getVillaReminders = (villaId) =>
  api.get(`/reminders/villa/${villaId}`)

// Analytics
export const getVillaMonthlyAnalytics = (villaId) =>
  api.get(`/analytics/villa/${villaId}/monthly`)

// Dashboard Operations - Unified dashboard data
export const getDashboardOperations = () =>
  api.get('/dashboard/operations')

// Dashboard Chart Data - Monthly bookings and revenue
export const getDashboardChartData = () =>
  api.get('/dashboard/chart-data')

// Non-Exclusive Bookings
export const getNonExclusiveBookings = (params = {}) => {
  const queryParams = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '' && value !== 'all')
  )
  const queryString = queryParams.toString()
  return api.get(`/non-exclusive-bookings${queryString ? `?${queryString}` : ''}`)
    .then(response => {
      return response;
    })
}

export const getNonExclusiveBooking = (bookingId) =>
  api.get(`/non-exclusive-bookings/${bookingId}`)

export const createNonExclusiveBooking = (data) =>
  api.post('/non-exclusive-bookings', data)

export const updateNonExclusiveBooking = (bookingId, data) =>
  api.put(`/non-exclusive-bookings/${bookingId}`, data)

export const updateNonExclusiveBookingNotes = (bookingId, notes) =>
  api.put(`/non-exclusive-bookings/${bookingId}/notes`, { notes })

export const deleteNonExclusiveBooking = (bookingId) =>
  api.delete(`/non-exclusive-bookings/${bookingId}`)

// Non-Exclusive Bookings Financial
export const getNonExclusiveBookingsFinancialSummary = (params = {}) => {
  const queryParams = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '' && value !== 'all')
  )
  const queryString = queryParams.toString()
  return api.get(`/non-exclusive-bookings/financial-summary${queryString ? `?${queryString}` : ''}`)
}

export const getNonExclusiveBookingFinancialSummary = (bookingId) =>
  api.get(`/non-exclusive-bookings/${bookingId}/financial-summary`, { timeout: 30000 })

export const getExclusiveBookingsFinancialSummary = (params = {}) => {
  const queryParams = new URLSearchParams()
  if (params.villa_id) queryParams.append('villa_id', params.villa_id)
  if (params.owner_id) queryParams.append('owner_id', params.owner_id)
  if (params.start) queryParams.append('start', params.start)
  if (params.end) queryParams.append('end', params.end)
  const queryString = queryParams.toString()
  return api.get(`/exclusive-bookings/financial-summary${queryString ? `?${queryString}` : ''}`)
}

export const deleteNonExclusiveBookingPayment = (bookingId, paymentId) =>
  api.delete(`/non-exclusive-bookings/${bookingId}/payments/${paymentId}`)

export const getNonExclusiveBookingsRevenueTrends = (params = {}) => {
  const queryParams = new URLSearchParams()
  if (params.villa_id) queryParams.append('villa_id', params.villa_id)
  if (params.start) queryParams.append('start', params.start)
  if (params.end) queryParams.append('end', params.end)
  const queryString = queryParams.toString()
  return api.get(`/non-exclusive-bookings/revenue-trends${queryString ? `?${queryString}` : ''}`)
}

// Non-Exclusive Booking Payments
export const getNonExclusiveBookingPayments = (bookingId) =>
  api.get(`/non-exclusive-bookings/${bookingId}/payments`)

export const createNonExclusiveBookingPayment = (bookingId, data) =>
  api.post(`/non-exclusive-bookings/${bookingId}/payments`, data)

// Non-Exclusive Booking Owner Payouts
export const getNonExclusiveBookingOwnerPayouts = (bookingId) =>
  api.get(`/non-exclusive-bookings/${bookingId}/owner-payouts`)

export const createNonExclusiveBookingOwnerPayout = (bookingId, data) =>
  api.post(`/non-exclusive-bookings/${bookingId}/owner-payouts`, data)

// Non-Exclusive Payouts
export const getNonExclusivePayoutsByVilla = (month) =>
  api.get(`/non-exclusive-payouts/by-villa?month=${month}`)

export const getNonExclusivePayoutsByOwnerAgent = (month) =>
  api.get(`/non-exclusive-payouts/by-owner-agent?month=${month}`)

export const markNonExclusivePayoutPaid = (payload) =>
  api.post('/non-exclusive-payouts/mark-paid', payload)

// Non-Exclusive Booking Payout History
export const getNonExclusiveBookingPayoutHistory = (bookingId) =>
  api.get(`/non-exclusive-bookings/${bookingId}/payout-history`)

export const createNonExclusiveBookingPayoutHistory = (bookingId, data) =>
  api.post(`/non-exclusive-bookings/${bookingId}/payout-history`, data)

// Non-Exclusive Booking-Level Payouts (one row per booking)
export const getNonExclusivePayoutsByVillaMonth = (month) =>
  api.get(`/non-exclusive-payouts/by-villa-bookings?month=${month}`)

export const getNonExclusivePayoutsByOwnerAgentMonth = (month) =>
  api.get(`/non-exclusive-payouts/by-owner-agent-bookings?month=${month}`)

// Search
export const search = (query) =>
  api.get(`/search?q=${encodeURIComponent(query)}`)

// ============ Embedded Email — Phase 1 ============
export async function listMailboxAttachments() {
  const r = await api.get('/mailbox-attachments');
  return r.data;
}
export async function createMailboxAttachment(payload) {
  const r = await api.post('/mailbox-attachments', payload);
  return r.data;
}
export async function updateMailboxAttachment(id, patch) {
  const r = await api.patch(`/mailbox-attachments/${id}`, patch);
  return r.data;
}
export async function deleteMailboxAttachment(id) {
  await api.delete(`/mailbox-attachments/${id}`);
}
// Additional Guest Charges — optional per-booking add-on rows (app table only; never
// changes the main booking totals/taxes/commission resolution).
export async function listAdditionalCharges(bookingType, bookingId, commissionPercent) {
  const params = { bookingType, bookingId };
  if (commissionPercent !== undefined) params.commissionPercent = commissionPercent;
  const r = await api.get('/booking-additional-charges', { params });
  return r.data; // { rows, summary? }
}
export async function createAdditionalCharge(bookingType, bookingId, row) {
  const r = await api.post('/booking-additional-charges', { bookingType, bookingId, ...row });
  return r.data;
}
export async function updateAdditionalCharge(id, row) {
  const r = await api.put(`/booking-additional-charges/${encodeURIComponent(id)}`, row);
  return r.data;
}
export async function deleteAdditionalCharge(id) {
  const r = await api.delete(`/booking-additional-charges/${encodeURIComponent(id)}`);
  return r.data;
}
export async function fetchInbox(params = {}) {
  const r = await api.get('/mail/inbox', { params, timeout: MAIL_READ_TIMEOUT });
  return r.data;
}
export async function fetchThread(threadId) {
  const r = await api.get(`/mail/thread/${encodeURIComponent(threadId)}`, { timeout: MAIL_READ_TIMEOUT });
  return r.data;
}
// Phase 1E — personal email folders (label-backed; app-local, never touches server mail).
export async function getEmailLabels() {
  const r = await api.get('/mail/labels');
  return Array.isArray(r.data) ? r.data : [];
}
export async function createEmailLabel(name, color) {
  const r = await api.post('/mail/labels', { name, color });
  return r.data;
}
export async function updateEmailLabel(labelId, patch) {
  const r = await api.put(`/mail/labels/${encodeURIComponent(labelId)}`, patch);
  return r.data;
}
export async function deleteEmailLabel(labelId) {
  const r = await api.delete(`/mail/labels/${encodeURIComponent(labelId)}`);
  return r.data;
}
export async function applyEmailLabelToThread(threadId, labelId) {
  const r = await api.post(`/mail/threads/${encodeURIComponent(threadId)}/labels`, { labelId });
  return r.data;
}
export async function removeEmailLabelFromThread(threadId, labelId) {
  const r = await api.delete(`/mail/threads/${encodeURIComponent(threadId)}/labels/${encodeURIComponent(labelId)}`);
  return r.data;
}
export async function testMailboxConnection(payload) {
  const r = await api.post('/mailbox-attachments/test', payload);
  return r.data;
}
export async function sendEmail(payload) {
  const r = await api.post('/mail/send', payload);
  return r.data;
}
export async function replyToThread(payload) {
  const r = await api.post('/mail/send', payload);
  return r.data;
}
// Forward an email — same endpoint as send/reply; a semantic wrapper so callers read clearly.
export async function forwardEmail(payload) {
  const r = await api.post('/mail/send', payload);
  return r.data;
}
// Contact autocomplete for the composer — searches clients/contacts/owners/agents.
export async function searchContacts(q) {
  const r = await api.get('/contacts/search', { params: { q } });
  return r.data; // { results: [{ id, name, email, type }] }
}
// Quick-reply templates (composer "Templates" dropdown).
export async function fetchQuickReplies() {
  const r = await api.get('/quick-replies');
  return r.data;
}
export async function createQuickReply(payload) {
  const r = await api.post('/quick-replies', payload);
  return r.data;
}
export async function updateQuickReply(id, patch) {
  const r = await api.patch(`/quick-replies/${id}`, patch);
  return r.data;
}
export async function deleteQuickReply(id) {
  await api.delete(`/quick-replies/${id}`);
}
// One-click Email → Lead conversion from the inbox.
export async function convertEmailToLead(emailId) {
  const r = await api.post('/mail/convert-to-lead', { email_id: emailId });
  return r.data; // { lead_id, client_id, lead_url, linked_entity_name }
}
// AI "Draft reply": Lilli classifies the email, then writes a STRICTLY GROUNDED draft for
// the operator to review. Optional tone rewrites it. Never sends. Returns the full
// metadata: { draft (HTML, '' when not drafted), classification, confidence, warnings,
// missingFacts }.
export async function draftReply(messageId, threadId, tone) {
  const { data } = await api.post('/mail/draft-reply', { messageId, threadId, tone: tone || undefined });
  return {
    draft: data.draft || '',
    classification: data.classification || 'unknown',
    confidence: typeof data.confidence === 'number' ? data.confidence : null,
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
    missingFacts: Array.isArray(data.missingFacts) ? data.missingFacts : [],
    // Explicit gate from the backend; fall back to "draft is non-empty" for back-compat
    // if an older backend (pre-shouldDraft) is answering.
    shouldDraft: typeof data.shouldDraft === 'boolean' ? data.shouldDraft : !!data.draft,
  };
}
// AI booking proposal for one or more villas (side-by-side when >1). FACTS are rendered
// deterministically from real rates by the backend; the AI writes only intro/closing.
// Accepts a single id or an array. Never sends. Returns { draft (HTML), warnings,
// linkedVillas: [{id,name,url,valid}] }.
export async function draftQuote(messageId, villaIds) {
  const arr = Array.isArray(villaIds) ? villaIds : [villaIds];
  const { data } = await api.post('/mail/draft-quote', { messageId, villaIds: arr });
  return {
    draft: data.draft || '',
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
    linkedVillas: Array.isArray(data.linkedVillas) ? data.linkedVillas : [],
    missingFacts: Array.isArray(data.missingFacts) ? data.missingFacts : [],
    quoteType: data.quoteType || (Array.isArray(arr) && arr.length > 1 ? 'multi_villa' : 'single_villa'),
    // Phase 1D — extracted stay dates + per-villa date-aware rate resolution (display-only).
    stayDates: data.stayDates || null,
    rateResolution: Array.isArray(data.rateResolution) ? data.rateResolution : [],
  };
}
// AI 2-3 bullet summary of a whole thread.
export async function summarizeThread(threadId) {
  const { data } = await api.post('/mail/summarize-thread', { threadId });
  return data.summary;
}
// AI action-item extraction from a single email → [{ action, who, deadline }].
export async function extractActions(messageId) {
  const { data } = await api.post('/mail/extract-actions', { messageId });
  return data.actions || [];
}
export async function markEmailRead(id, seen = true) {
  const r = await api.patch(`/mail/read/${encodeURIComponent(id)}`, { seen });
  return r.data;
}
export async function moveThread(threadId, folder = 'Trash') {
  const r = await api.patch(`/mail/trash/${encodeURIComponent(threadId)}`, { folder }, { timeout: MAIL_READ_TIMEOUT });
  return r.data;
}
function bulkActionError(message, data) {
  const err = new Error(data?.error || message);
  err.response = { data: data || { error: message } };
  return err;
}

// Bulk "delete" = move many threads to Trash (recoverable, app-level — never expunged).
// Partial success (some selections stale/already-moved) is SUCCESS with a partial flag,
// not a failure — only zero threads moved throws.
export async function bulkDeleteThreads(threadIds) {
  const r = await api.post('/mail/bulk-delete', { threadIds }, { timeout: MAIL_BULK_TIMEOUT });
  const data = r.data;
  const result = getBulkActionResult(data, threadIds.length, 'movedThreads');
  if (!result.ok) {
    throw bulkActionError('No selected emails were moved to Trash', data);
  }
  return { ...data, partial: result.partial }; // { deleted, threads, movedThreads, missingThreadIds, partial }
}
// Bulk restore = move many Trash/soft-deleted threads back to INBOX. Same partial-success
// model as bulk-delete; uses the real backend /bulk-restore endpoint (one request, not N).
export async function bulkRestoreThreads(threadIds) {
  const r = await api.post('/mail/bulk-restore', { threadIds }, { timeout: MAIL_BULK_TIMEOUT });
  const data = r.data;
  const result = getBulkActionResult(data, threadIds.length, 'restoredThreads');
  if (!result.ok) {
    throw bulkActionError('No selected emails were restored to the Inbox', data);
  }
  return { ...data, partial: result.partial }; // { restored, threads, restoredThreads, missingThreadIds, partial }
}
// Bulk "not spam" = move many Spam threads back to INBOX + save not-spam feedback in ONE
// request (replaces firing one unmark-spam call per thread). Partial-success safe.
export async function bulkNotSpamThreads(threadIds) {
  const r = await api.post('/mail/bulk-not-spam', { threadIds }, { timeout: MAIL_BULK_TIMEOUT });
  const data = r.data;
  const result = getBulkActionResult(data, threadIds.length, 'updatedThreads');
  if (!result.ok) {
    throw bulkActionError('No selected emails were marked not spam', data);
  }
  return { ...data, partial: result.partial }; // { updated, threads, updatedThreads, missingThreadIds, partial, folder }
}
// Permanently remove selected rows from Trash/Spam only.
export async function bulkPermanentDeleteThreads(threadIds, folder) {
  const r = await api.post('/mail/bulk-permanent-delete', { threadIds, folder }, { timeout: MAIL_BULK_TIMEOUT });
  const data = r.data;
  const result = getBulkPermanentDeleteResult(data, threadIds.length);
  if (!result.ok) {
    throw bulkActionError('No selected emails were permanently deleted', data);
  }
  return { ...data, partial: result.partial }; // { deleted, threads, deletedThreads, folder, missingThreadIds }
}
// Star / unstar a whole thread.
export async function starThread(threadId, starred) {
  const r = await api.patch('/mail/star', { threadId, starred });
  return r.data;
}
// Bulk mark threads read/unread.
export async function bulkMarkRead(threadIds, read) {
  const r = await api.post('/mail/bulk-mark-read', { threadIds, read }, { timeout: MAIL_BULK_TIMEOUT });
  return r.data; // { updated, read }
}
// Permanently empty the Spam or Trash folder.
export async function emptyFolder(folder) {
  const r = await api.post('/mail/empty-folder', { folder }, { timeout: MAIL_BULK_TIMEOUT });
  return r.data; // { deleted, folder }
}
// Per-user email signature (auto-appended in compose/reply).
export async function getMySignature() {
  const r = await api.get('/signatures/mine');
  return r.data; // signature row or null
}
export async function putMySignature(payload) {
  const r = await api.put('/signatures/mine', payload);
  return r.data;
}
// Upload a signature profile photo (multipart) → returns its absolute URL.
export async function uploadSignaturePhoto(file) {
  const fd = new FormData();
  fd.append('photo', file);
  const r = await api.post('/signatures/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data.photo_url;
}
// Manual spam controls — move a thread to / out of the Spam folder.
export async function markThreadSpam(threadId) {
  const r = await api.post(`/mail/mark-spam/${encodeURIComponent(threadId)}`);
  return r.data;
}
export async function unmarkThreadSpam(threadId) {
  const r = await api.post(`/mail/unmark-spam/${encodeURIComponent(threadId)}`);
  return r.data;
}
export async function searchEmails(params = {}) {
  const r = await api.get('/mail/search', { params, timeout: MAIL_READ_TIMEOUT });
  return r.data;
}
// Phase C: emails linked to a CRM entity (lead|client|owner|agent).
export async function getEmailsByEntity(type, id, limit = 50) {
  const r = await api.get('/mail/by-entity', { params: { type, id, limit } });
  return r.data; // { data: [...] }
}

// ==================== AGENDA — Reservation Operations Calendar ====================
// Unified operations agenda (/api/agenda): booking-derived operational dates
// (arrivals, departures, guest_payment_due, insurance_due, security_refund_due,
// owner_payout_pending, hold_expiry) + manual user tasks (agent_tasks). Operational
// items are read-only; only `task` events are editable. See backend/routes/agenda.js.

// Local YYYY-MM-DD — NOT toISOString(), which converts to UTC and can roll the date
// across midnight depending on timezone.
const ymdLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDaysLocal = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// getAgenda — defaults to today..today+30 (local dates). Callers that need the
// "Overdue" bucket widen `from` into the past (the backend windows by date only).
export const getAgenda = (params = {}) => {
  const today = new Date();
  const defaults = { from: ymdLocal(today), to: ymdLocal(addDaysLocal(today, 30)) };
  return api.get('/agenda', { params: { ...defaults, ...params } });
};
export const getAgendaList = (params = {}) => api.get('/agenda/list', { params });
// Tasks linked to one booking (bookingId [+bookingType]) or one lead (leadId) —
// thin wrappers over /agenda/list's additive by-entity filters. Own-scoped server-side.
export const getTasksForBooking = (bookingId, bookingType) =>
  api.get('/agenda/list', { params: { bookingId, ...(bookingType ? { bookingType } : {}) } });
export const getTasksForLead = (leadId) => api.get('/agenda/list', { params: { leadId } });
// Tasks linked to any of a client's leads/bookings (derived server-side; agent_tasks
// has no client_id column).
export const getTasksForClient = (clientId) => api.get(`/agenda/by-client/${clientId}`);
export const createAgendaTask = (payload) => api.post('/agenda', payload);
export const updateAgendaTask = (id, payload) => api.patch(`/agenda/${id}`, payload);
export const markAgendaTaskDone = (id) => api.patch(`/agenda/${id}`, { status: 'DONE' });
export const deleteAgendaTask = (id) => api.delete(`/agenda/${id}`);

// getCalendarAlertSummary — in-app urgent-alert counts for the sidebar badge + the
// Dashboard "Reservation Operations" card (Phase 7). Counts ONLY the trusted alert
// types that are due today or overdue (start <= today); tasks must be not-DONE.
// Excludes arrivals/departures and owner_payout_pending. Window today-365..today+30
// (catches overdue + near-future). Returns { total, tasks, payments, refunds,
// insurance, holds }. Local YYYY-MM-DD (no UTC shift). RBAC = whatever /api/agenda
// returns for the logged-in user (tasks are backend-scoped).
export const getCalendarAlertSummary = async () => {
  const today = ymdLocal(new Date());
  const from = ymdLocal(addDaysLocal(new Date(), -365));
  const to = ymdLocal(addDaysLocal(new Date(), 30));
  const res = await getAgenda({ from, to });
  const events = Array.isArray(res.data?.events) ? res.data.events : [];
  return summarizeCalendarAlerts(events, today);   // pure count logic — utils/calendarLogic (unit-tested)
};

export default api
